import React, { useEffect, useState } from "react";
import { DashboardShell, StatCard } from "@/components/DashboardShell";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { LayoutDashboard, FileText, CreditCard, Users, ScanLine, IndianRupee, Activity, CheckCircle2, AlertTriangle } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { toast } from "sonner";

const NAV = [
  { key: "home", label: "Overview", to: "/admin", icon: LayoutDashboard },
  { key: "users", label: "Users & Papers", to: "/admin/users", icon: Users },
  { key: "plans", label: "Purchase Plans", to: "/admin/plans", icon: CreditCard },
  { key: "papers", label: "Question Papers", to: "/admin/papers", icon: FileText },
  { key: "failed", label: "Failed evaluations", to: "/admin/failed-evaluations", icon: AlertTriangle },
];

const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [mode, setMode] = useState("test");

  useEffect(() => {
    api.get("/stats/admin").then((r) => setStats(r.data)).catch(() => {});
    api.get("/settings/mode").then((r) => setMode(r.data.mode)).catch(() => {});
  }, []);

  const toggleMode = async (checked) => {
    const newMode = checked ? "production" : "test";
    try {
      await api.put("/settings/mode", { mode: newMode });
      setMode(newMode);
      toast.success(`Switched to ${newMode} mode`);
    } catch {
      toast.error("Failed to switch mode");
    }
  };

  const t = stats?.totals;

  return (
    <DashboardShell nav={NAV} title="Platform Overview">
      <div className="flex items-center justify-between mb-6 bg-background border border-border rounded-lg px-6 py-4" data-testid="mode-toggle-panel">
        <div>
          <p className="overline text-muted-foreground">System mode</p>
          <p className="text-sm text-muted-foreground mt-1">
            {mode === "test" ? "Test mode — payments bypassed, notifications mocked." : "Production mode — Razorpay & live notifications active."}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-sm font-medium ${mode === "test" ? "text-primary" : "text-muted-foreground"}`}>Test</span>
          <Switch checked={mode === "production"} onCheckedChange={toggleMode} data-testid="mode-switch" />
          <span className={`text-sm font-medium ${mode === "production" ? "text-primary" : "text-muted-foreground"}`}>Production</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:gap-6 lg:grid-cols-4">
        <StatCard testId="admin-stat-evaluated" label="Scripts evaluated" value={t?.scripts_evaluated ?? 0} sub={`${t?.scripts_total ?? 0} total`} icon={CheckCircle2} />
        <StatCard testId="admin-stat-pages" label="Pages scanned" value={t?.pages_scanned ?? 0} icon={ScanLine} accent="text-[hsl(var(--chart-2))]" />
        <StatCard testId="admin-stat-students" label="Students" value={t?.students ?? 0} sub={`${t?.active_subscriptions ?? 0} active plans`} icon={Users} accent="text-[hsl(var(--chart-3))]" />
        <StatCard testId="admin-stat-revenue" label="Revenue" value={`₹${t?.revenue_inr ?? 0}`} icon={IndianRupee} accent="text-[hsl(var(--chart-5))]" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 bg-background border border-border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4"><Activity className="h-4 w-4 text-primary" /><p className="overline text-muted-foreground">Evaluations over time</p></div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={256}>
              <LineChart data={stats?.evaluations_over_time || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-background border border-border rounded-lg p-6">
          <p className="overline text-muted-foreground mb-4">By subject</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={256}>
              <PieChart>
                <Pie data={stats?.by_subject || []} dataKey="count" nameKey="subject" cx="50%" cy="50%" outerRadius={80} label>
                  {(stats?.by_subject || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-background border border-border rounded-lg p-6 mt-6">
        <div className="flex items-center gap-2 mb-4"><Users className="h-4 w-4 text-primary" /><p className="overline text-muted-foreground">Active subscriptions & revenue by plan</p></div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={288}>
            <BarChart data={stats?.plan_breakdown || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="plan" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip />
              <Bar dataKey="count" fill="hsl(var(--chart-1))" name="Subscriptions" radius={[4, 4, 0, 0]} />
              <Bar dataKey="revenue" fill="hsl(var(--chart-2))" name="Revenue (₹)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </DashboardShell>
  );
}
